/**
 * Connection quality monitoring utilities.
 * Tracks latency metrics and computes connection quality.
 */
/** Thresholds for connection quality classification */
const QUALITY_THRESHOLDS = {
    excellent: { maxLatency: 50, maxJitter: 10 },
    good: { maxLatency: 100, maxJitter: 30 },
    fair: { maxLatency: 200, maxJitter: 60 },
    poor: { maxLatency: 400, maxJitter: 100 },
    // Anything above poor thresholds is critical
};
/** Default metrics for when no data is available */
export const DEFAULT_METRICS = {
    latency: 0,
    averageLatency: 0,
    jitter: 0,
    packetLoss: 0,
    quality: 'good',
    lastPingTime: 0,
};
/**
 * Connection monitor that tracks latency metrics over time.
 */
export class ConnectionMonitor {
    samples = [];
    maxSamples;
    missedPings = 0;
    totalPings = 0;
    lastPingTime = 0;
    listeners = new Set();
    constructor(maxSamples = 20) {
        this.maxSamples = maxSamples;
    }
    /**
     * Record a latency sample (call when PONG received)
     */
    recordLatency(latencyMs) {
        this.samples.push(latencyMs);
        if (this.samples.length > this.maxSamples) {
            this.samples.shift();
        }
        this.lastPingTime = Date.now();
        this.totalPings++;
        this.notifyListeners();
    }
    /**
     * Record a missed ping (no PONG received)
     */
    recordMissedPing() {
        this.missedPings++;
        this.totalPings++;
        this.notifyListeners();
    }
    /**
     * Get current connection metrics
     */
    getMetrics() {
        if (this.samples.length === 0) {
            return DEFAULT_METRICS;
        }
        const latency = this.samples[this.samples.length - 1] ?? 0;
        const averageLatency = this.calculateAverage();
        const jitter = this.calculateJitter();
        const packetLoss = this.totalPings > 0 ? (this.missedPings / this.totalPings) * 100 : 0;
        const quality = this.calculateQuality(averageLatency, jitter);
        return {
            latency,
            averageLatency,
            jitter,
            packetLoss,
            quality,
            lastPingTime: this.lastPingTime,
        };
    }
    /**
     * Subscribe to metric updates
     */
    subscribe(callback) {
        this.listeners.add(callback);
        return () => this.listeners.delete(callback);
    }
    /**
     * Reset all metrics
     */
    reset() {
        this.samples = [];
        this.missedPings = 0;
        this.totalPings = 0;
        this.lastPingTime = 0;
        this.notifyListeners();
    }
    calculateAverage() {
        if (this.samples.length === 0)
            return 0;
        const sum = this.samples.reduce((a, b) => a + b, 0);
        return Math.round(sum / this.samples.length);
    }
    calculateJitter() {
        if (this.samples.length < 2)
            return 0;
        // Calculate average absolute difference between consecutive samples
        let totalDiff = 0;
        for (let i = 1; i < this.samples.length; i++) {
            totalDiff += Math.abs((this.samples[i] ?? 0) - (this.samples[i - 1] ?? 0));
        }
        return Math.round(totalDiff / (this.samples.length - 1));
    }
    calculateQuality(avgLatency, jitter) {
        if (avgLatency <= QUALITY_THRESHOLDS.excellent.maxLatency &&
            jitter <= QUALITY_THRESHOLDS.excellent.maxJitter) {
            return 'excellent';
        }
        if (avgLatency <= QUALITY_THRESHOLDS.good.maxLatency &&
            jitter <= QUALITY_THRESHOLDS.good.maxJitter) {
            return 'good';
        }
        if (avgLatency <= QUALITY_THRESHOLDS.fair.maxLatency &&
            jitter <= QUALITY_THRESHOLDS.fair.maxJitter) {
            return 'fair';
        }
        if (avgLatency <= QUALITY_THRESHOLDS.poor.maxLatency &&
            jitter <= QUALITY_THRESHOLDS.poor.maxJitter) {
            return 'poor';
        }
        return 'critical';
    }
    notifyListeners() {
        const metrics = this.getMetrics();
        for (const listener of this.listeners) {
            listener(metrics);
        }
    }
}
/**
 * Get display properties for a connection quality level
 */
export function getQualityDisplay(quality) {
    switch (quality) {
        case 'excellent':
            return { label: 'Excellent', color: '#22c55e', bars: 5 };
        case 'good':
            return { label: 'Good', color: '#84cc16', bars: 4 };
        case 'fair':
            return { label: 'Fair', color: '#eab308', bars: 3 };
        case 'poor':
            return { label: 'Poor', color: '#f97316', bars: 2 };
        case 'critical':
            return { label: 'Critical', color: '#ef4444', bars: 1 };
    }
}
/**
 * Get adaptive settings based on connection quality
 */
export function getAdaptiveSettings(quality) {
    switch (quality) {
        case 'excellent':
        case 'good':
            return {
                updateInterval: 100,
                enableAnimations: true,
                compressMessages: false,
                extendTimeouts: false,
            };
        case 'fair':
            return {
                updateInterval: 200,
                enableAnimations: true,
                compressMessages: false,
                extendTimeouts: false,
            };
        case 'poor':
            return {
                updateInterval: 500,
                enableAnimations: false,
                compressMessages: true,
                extendTimeouts: true,
            };
        case 'critical':
            return {
                updateInterval: 1000,
                enableAnimations: false,
                compressMessages: true,
                extendTimeouts: true,
            };
    }
}
//# sourceMappingURL=connection.js.map